/**
 * BENumericInput — champ numérique tolérant format belge (virgule + point).
 * Corrige Bug 1 (0 indélébile) et Bug 2 (virgule refusée).
 *
 * Comportement :
 * - Affiche la valeur formatée belge (virgule décimale) par défaut.
 * - Au focus : sélectionne tout le contenu (permet remplacement immédiat) + affiche la valeur brute.
 * - Pendant la frappe : accepte chiffres, virgule, point. La virgule est convertie en point à la validation.
 * - Au blur : parse la valeur (`parseFloat(str.replace(',', '.'))`). Vide → 0.
 * - Émet la valeur numérique via onChange(number).
 *
 * Utilisation :
 *   <BENumericInput value={line.quantity} onChange={(n) => onUpdate({ quantity: n })} />
 */
import { forwardRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

// Regex de saisie : chiffres, un séparateur (virgule ou point), 0-4 décimales
const VALID_INPUT_RE = /^-?\d*([.,]\d{0,4})?$/;

function toDisplay(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "";
  const num = Number(n);
  if (num === 0) return "0";
  // Affichage neutre : virgule décimale, pas d'espace insécable (celui-ci est pour le format € final)
  return String(num).replace(".", ",");
}

const BENumericInput = forwardRef(function BENumericInput(
  { value, onChange, disabled, className, "data-testid": dtid, ...rest },
  ref,
) {
  const [draft, setDraft] = useState(toDisplay(value));
  const [focused, setFocused] = useState(false);

  // Sync externe uniquement quand non focus (évite d'écraser la frappe)
  useEffect(() => {
    if (!focused) setDraft(toDisplay(value));
  }, [value, focused]);

  const commit = () => {
    const cleaned = draft.trim().replace(",", ".");
    if (cleaned === "" || cleaned === "-") {
      onChange(0);
      setDraft("0");
      return;
    }
    const n = parseFloat(cleaned);
    const final = Number.isFinite(n) ? n : 0;
    onChange(final);
    setDraft(toDisplay(final));
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      className={className}
      data-testid={dtid}
      onFocus={(e) => {
        setFocused(true);
        // Sélectionne tout à la focalisation → permet de taper directement pour remplacer
        e.target.select();
      }}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || VALID_INPUT_RE.test(v)) {
          setDraft(v);
        }
      }}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      {...rest}
    />
  );
});

export default BENumericInput;
